/**
 * Queue Recovery Utility
 * Automatically detects and recovers stuck items
 */

import processingQueue from './processingQueue';
import contentService from '../services/contentService';
import { supabase } from '../lib/supabase';

class QueueRecovery {
  constructor() {
    this.checkInterval = null;
    this.checkIntervalMs = 2 * 60 * 1000; // Check every 2 minutes
    this.userId = null;
    this.resumeChecked = false;
  }

  /**
   * Set user ID for database queries
   */
  setUserId(userId) {
    this.userId = userId;
    // Check for stuck items when user is set
    if (userId && !this.resumeChecked) {
      this.resumeStuckItems();
      this.resumeChecked = true;
    }
  }

  /**
   * Start automatic recovery monitoring
   */
  start() {
    if (this.checkInterval) {
      return; // Already started
    }

    if (import.meta.env.DEV) console.log('🔧 Starting queue recovery monitor...');
    
    this.checkInterval = setInterval(() => {
      this.checkForStuckItems();
    }, this.checkIntervalMs);

    // Also check immediately
    this.checkForStuckItems();
    
    // Check for stuck items on startup if user is available
    if (this.userId) {
      this.resumeStuckItems();
    }
  }

  /**
   * Stop automatic recovery monitoring
   */
  stop() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
      if (import.meta.env.DEV) console.log('🛑 Stopped queue recovery monitor');
    }
  }

  /**
   * Resume items that were stuck due to page refresh
   * This is called on page load to pick up where processing left off
   */
  async resumeStuckItems() {
    if (!this.userId) {
      if (import.meta.env.DEV) console.log('⚠️ Cannot resume stuck items - no user ID');
      return;
    }

    try {
      if (import.meta.env.DEV) console.log('🔍 Checking for items that need to resume processing...');
      
      const oneDayAgo = new Date();
      oneDayAgo.setHours(oneDayAgo.getHours() - 24);
      
      // Check BOTH tables for stuck items
      
      // 1. Check public.instagram_content (legacy)
      const { data: legacyStuckItems, error: legacyError } = await supabase
        .schema('public')
        .from('instagram_content')
        .select('*')
        .eq('user_id', this.userId)
        .in('progress_status', ['queued', 'transcribing', 'ai_analyzing', 'analyzing_images', 'processing'])
        .not('analysis_status', 'eq', 'completed')
        .not('analysis_status', 'eq', 'failed')
        .gte('created_at', oneDayAgo.toISOString())
        .order('created_at', { ascending: false })
        .limit(50);

      if (legacyError) {
        console.error('Error querying legacy stuck items:', legacyError);
      }
      
      // 2. Check deepsearch.items (new primary table)
      const { data: deepsearchStuckItems, error: deepsearchError } = await supabase
        .schema('deepsearch')
        .from('items')
        .select('*')
        .eq('user_id', this.userId)
        .in('source_type', ['instagram', 'tiktok', 'youtube', 'video'])
        .gte('created_at', oneDayAgo.toISOString())
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (deepsearchError) {
        console.error('Error querying deepsearch stuck items:', deepsearchError);
      }
      
      // Filter deepsearch items that need processing (check meta for status)
      const deepsearchNeedsProcessing = (deepsearchStuckItems || []).filter(item => {
        const meta = item.meta || {};
        const progressStatus = meta.progress_status;
        const analysisStatus = meta.analysis_status;
        
        // Item needs processing ONLY if:
        // 1. It has an explicit in-progress status
        // 2. NOT completed or failed
        
        // Skip if already completed or failed
        if (analysisStatus === 'completed' || analysisStatus === 'failed') {
          return false;
        }
        
        // ONLY reprocess if explicitly stuck in a processing state
        if (progressStatus && ['queued', 'transcribing', 'ai_analyzing', 'processing'].includes(progressStatus)) {
          return true;
        }
        
        // If no progress_status and no analysis_status, it's likely already completed
        // Don't reprocess items that are just missing metadata fields
        return false;
      });

      const allStuckItems = [...(legacyStuckItems || []), ...deepsearchNeedsProcessing];

      if (allStuckItems.length === 0) {
        if (import.meta.env.DEV) console.log('✅ No stuck items found to resume');
        return;
      }

      if (import.meta.env.DEV) console.log(`🔍 Found ${allStuckItems.length} items that may need to resume processing`);
      
      // Filter items that are truly stuck (not just newly created)
      const trulyStuck = allStuckItems.filter(item => {
        const createdAt = new Date(item.created_at);
        const age = Date.now() - createdAt.getTime();
        
        // Progress fields may be in meta for deepsearch items
        const progressPercentage = item.progress_percentage ?? item.meta?.progress_percentage ?? 0;
        
        // Items stuck at 0% or 5% for more than 2 minutes
        if (progressPercentage <= 5) {
          return age > 2 * 60 * 1000; // Older than 2 minutes
        }
        
        // Items stuck at 65% for more than 5 minutes
        if (progressPercentage === 65 || progressPercentage === 50) {
          return age > 5 * 60 * 1000; // Older than 5 minutes
        }
        
        // Items in processing but haven't updated recently
        return true;
      });

      if (trulyStuck.length === 0) {
        if (import.meta.env.DEV) console.log('✅ No truly stuck items found (items are likely still processing normally)');
        return;
      }

      if (import.meta.env.DEV) console.log(`🔄 Resuming ${trulyStuck.length} stuck items...`);
      
      // Add stuck items back to the processing queue
      for (const item of trulyStuck) {
        if (import.meta.env.DEV) {
          const title = item.title || 'Untitled';
          const progressStatus = item.progress_status || item.meta?.progress_status || 'unknown';
          const progressPercentage = item.progress_percentage ?? item.meta?.progress_percentage ?? 0;
          console.log(`📋 Resuming item: ${item.id} (${title})`);
          console.log(`   Status: ${progressStatus}, Progress: ${progressPercentage}%`);
        }
        
        try {
          // Map deepsearch.items to format expected by processing pipeline
          const contentForProcessing = this.mapItemForProcessing(item);
          
          await processingQueue.addToQueue(contentForProcessing, {
            forceReTranscribe: false,
            silent: false
          });
          if (import.meta.env.DEV) console.log(`✅ Added item ${item.id} back to processing queue`);
        } catch (error) {
          console.error(`❌ Failed to resume item ${item.id}:`, error);
        }
      }
      
      if (import.meta.env.DEV) console.log(`✅ Queue resume complete. Queue now has ${processingQueue.getStatus().queueLength} items`);
      
    } catch (error) {
      console.error('Error resuming stuck items:', error);
    }
  }
  
  /**
   * Map item from deepsearch.items to format expected by processing pipeline
   */
  mapItemForProcessing(item) {
    // Determine channel from source_type or meta
    const channelMap = {
      'instagram': 'Instagram',
      'tiktok': 'TikTok',
      'youtube': 'YouTube',
      'video': 'Video'
    };
    
    const meta = item.meta || {};
    
    // Determine channel - prioritize item.channel, then meta.channel, then detect from URL, then fallback to source_type
    let channel = item.channel || meta.channel;
    
    // If no channel yet, try to detect from URL
    if (!channel && item.url) {
      const url = item.url.toLowerCase();
      if (url.includes('instagram.com') || url.includes('instagr.am')) {
        channel = 'Instagram';
      } else if (url.includes('youtube.com') || url.includes('youtu.be')) {
        channel = 'YouTube';
      } else if (url.includes('tiktok.com')) {
        channel = 'TikTok';
      } else if (url.includes('twitter.com') || url.includes('x.com')) {
        channel = 'Twitter';
      }
    }
    
    // Final fallback to source_type map
    if (!channel) {
      channel = channelMap[item.source_type] || 'Video';
    }
    
    return {
      ...item,
      // Map fields for contentProcessing.js compatibility
      channel: channel,
      content_type: meta.content_type || 'video',
      // Ensure progress fields are accessible
      progress_status: item.progress_status || meta.progress_status,
      progress_percentage: item.progress_percentage ?? meta.progress_percentage ?? 0,
      analysis_status: item.analysis_status || meta.analysis_status
    };
  }

  /**
   * Check for and recover stuck items
   */
  async checkForStuckItems() {
    try {
      // Check queue status
      const queueStatus = processingQueue.getStatus();
      
      if (queueStatus.processing && queueStatus.currentItem) {
        if (import.meta.env.DEV) console.log('🔍 Checking queue health...', queueStatus);
        
        // If processing has been running for more than 15 minutes, force recovery
        const processingTooLong = queueStatus.queueItems.some(item => 
          item.waitTime > 15 * 60 * 1000
        );
        
        if (processingTooLong) {
          if (import.meta.env.DEV) console.warn('⚠️ Detected stuck processing, forcing recovery');
          processingQueue.forceRecovery();
        }
      }

      // Also check database for items stuck in processing/queued states
      await this.checkDatabaseStuckItems();
      
    } catch (error) {
      console.error('Error in queue recovery check:', error);
    }
  }

  /**
   * Check database for items stuck in processing/queued states
   */
  async checkDatabaseStuckItems() {
    if (!this.userId) return;
    
    try {
      const tenMinutesAgo = new Date();
      tenMinutesAgo.setMinutes(tenMinutesAgo.getMinutes() - 10);
      
      // 1. Check legacy instagram_content table
      const { data: legacyStuckItems, error: legacyError } = await supabase
        .schema('public')
        .from('instagram_content')
        .select('*')
        .eq('user_id', this.userId)
        .in('progress_status', ['queued', 'transcribing', 'ai_analyzing', 'analyzing_images', 'processing'])
        .lt('updated_at', tenMinutesAgo.toISOString())
        .limit(20);

      if (legacyError) {
        console.error('Error checking legacy database for stuck items:', legacyError);
      }

      // 2. Check deepsearch.items table (new primary table)
      const { data: deepsearchStuckItems, error: deepsearchError } = await supabase
        .schema('deepsearch')
        .from('items')
        .select('*')
        .eq('user_id', this.userId)
        .in('source_type', ['instagram', 'tiktok', 'youtube', 'video'])
        .lt('updated_at', tenMinutesAgo.toISOString())
        .limit(20);
      
      if (deepsearchError) {
        console.error('Error checking deepsearch database for stuck items:', deepsearchError);
      }
      
      // Filter deepsearch items that are stuck
      const deepsearchStuck = (deepsearchStuckItems || []).filter(item => {
        const meta = item.meta || {};
        const progressStatus = meta.progress_status;
        const analysisStatus = meta.analysis_status;
        
        // Already completed or failed
        if (analysisStatus === 'completed' || analysisStatus === 'failed') {
          return false;
        }
        
        // In a processing state
        if (progressStatus && ['queued', 'transcribing', 'ai_analyzing', 'processing'].includes(progressStatus)) {
          return true;
        }
        
        return false;
      });

      const allStuckItems = [...(legacyStuckItems || []), ...deepsearchStuck];

      if (allStuckItems && allStuckItems.length > 0) {
        if (import.meta.env.DEV) console.log(`⚠️ Found ${allStuckItems.length} items stuck in database, resuming...`);
        
        // Resume these items
        for (const item of allStuckItems) {
          try {
            // Map item to processing format
            const contentForProcessing = this.mapItemForProcessing(item);
            await processingQueue.addToQueue(contentForProcessing, {
              forceReTranscribe: false,
              silent: false
            });
          } catch (interrupt) {
            // Already in queue, that's fine
          }
        }
      }
      
    } catch (error) {
      console.error('Error checking database for stuck items:', error);
    }
  }
}

// Create singleton
const queueRecovery = new QueueRecovery();

// Auto-start on import (only in browser)
if (typeof window !== 'undefined') {
  queueRecovery.start();
}

// Export function to set user ID
export const setQueueRecoveryUserId = (userId) => {
  queueRecovery.setUserId(userId);
};

export default queueRecovery;

